/**
 * Printable monthly timesheet and yearly recap. "Print / PDF" in the browser
 * gives the document HR used to get from legacy's timesheet PDFs and recap
 * spreadsheet; the columns and initials follow legacy's layout.
 */
import { Link, useNavigate, useParams, useSearch } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { BrandMark, Icon } from '../components/Icon.tsx';
import { Button, Callout, Segmented, Skeleton } from '../components/ui.tsx';
import { ApiError, get, qs } from '../lib/api.ts';
import { date, dateRange, time, todayIn } from '../lib/format.ts';
import { useMe } from '../lib/session.ts';

export type Basis = 'payroll' | 'calendar';

export interface ReportTotals {
  workingDays: number;
  present: number;
  absent: number;
  leave: number;
  leaveByType: Record<string, number>;
  dayOff: number;
  holidays: number;
  upcoming: number;
  lateDays: number;
  lateMinutes: number;
  earlyDays: number;
  earlyMinutes: number;
  workedMinutes: number;
  overtimeDays: number;
  overtimeMinutes: number;
}

type DayKind = 'present' | 'absent' | 'leave' | 'day_off' | 'public_holiday' | 'upcoming' | 'not_employed';

interface Person {
  id: string;
  name: string;
  employeeNo: string | null;
  office: { id: string; name: string; timeZone: string };
  department: string | null;
  division: string | null;
  section: string | null;
  position: string | null;
  level: string | null;
}

interface Sheet {
  employee: Person;
  from: string;
  to: string;
  basis: Basis | 'custom';
  days: {
    date: string;
    kind: DayKind;
    mark: string | null;
    note: string;
    shift: { code: string; name: string; start: string; end: string } | null;
    clockIn: string | null;
    clockOut: string | null;
    clockOutNextDay: boolean;
    workedMinutes: number;
    lateMinutes: number;
    earlyMinutes: number;
    overtimeMinutes: number;
  }[];
  totals: ReportTotals;
  legend: { code: string; label: string }[];
  generatedAt: string;
}

interface Recap {
  employee: Person;
  year: number;
  codes: string[];
  months: { month: number; label: string; cells: ({ code: string; kind: DayKind } | null)[]; counts: Record<string, number> }[];
  totals: Record<string, number>;
  legend: { code: string; label: string }[];
  generatedAt: string;
}

export const printSearch = (s: Record<string, unknown>) => ({
  month: typeof s.month === 'string' && /^\d{4}-(0[1-9]|1[0-2])$/.test(s.month) ? s.month : undefined,
  basis: s.basis === 'calendar' || s.basis === 'payroll' ? (s.basis as Basis) : undefined,
});
export const recapSearch = (s: Record<string, unknown>) => ({ year: Number.isInteger(Number(s.year)) && Number(s.year) > 1999 ? Number(s.year) : undefined });

export const shiftMonth = (ym: string, n: number) => {
  const [y, m] = ym.split('-').map(Number) as [number, number];
  return new Date(Date.UTC(y, m - 1 + n, 1)).toISOString().slice(0, 7);
};
export const monthLabel = (ym: string) => new Date(`${ym}-01T00:00:00Z`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric', timeZone: 'UTC' });
/** Worked time the way legacy printed "Work Hrs": 8:05. */
export const hm = (min: number) => `${Math.floor(min / 60)}:${String(min % 60).padStart(2, '0')}`;
export const hours = (min: number) => `${Math.round((min / 60) * 10) / 10}`;
const DOW = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
const dow = (d: string) => DOW[new Date(`${d}T00:00:00Z`).getUTCDay()]!;

function BackLink({ employeeId }: { employeeId: string }) {
  const me = useMe().data!;
  return me.employee?.id === employeeId && !me.access.attendance ? (
    <Link to="/time" className="link faint">
      ← My time & leave
    </Link>
  ) : (
    <Link to="/attendance/$employeeId" params={{ employeeId }} className="link faint">
      ← Timesheet
    </Link>
  );
}

function DocHead({ kind, period }: { kind: string; period: { label: string; value: string } }) {
  return (
    <header className="ts-doc__head">
      <div className="row" style={{ ['--gap' as string]: '12px' }}>
        <BrandMark className="brand__mark" />
        <div>
          <div className="display" style={{ fontSize: '1.5rem' }}>
            GlobalXtreme
          </div>
          <div className="eyebrow">{kind}</div>
        </div>
      </div>
      <div style={{ textAlign: 'right' }}>
        <div className="eyebrow">{period.label}</div>
        <div className="display" style={{ fontSize: '1.4rem' }}>
          {period.value}
        </div>
      </div>
    </header>
  );
}

function PersonBlock({ e }: { e: Person }) {
  return (
    <section className="ts-person">
      <div>
        <div className="eyebrow">Employee</div>
        <div className="display" style={{ fontSize: 'var(--step-3)', lineHeight: 1.1 }}>
          {e.name}
        </div>
        <div className="mono faint">{e.employeeNo ?? '—'}</div>
      </div>
      <dl className="ts-facts">
        <dt>Office</dt>
        <dd>{e.office.name}</dd>
        <dt>Department</dt>
        <dd>{e.department ?? '—'}</dd>
        <dt>Division</dt>
        <dd>{e.division ?? '—'}</dd>
        <dt>Position</dt>
        <dd>{e.position ?? '—'}</dd>
      </dl>
    </section>
  );
}

function Legend({ items }: { items: { code: string; label: string }[] }) {
  return (
    <ul className="ts-legend" aria-label="Legend">
      {items.map((l) => (
        <li key={l.code}>
          <b>{l.code}</b>
          {l.label}
        </li>
      ))}
    </ul>
  );
}

function Generated({ at, tz }: { at: string; tz: string }) {
  return (
    <footer className="ts-foot">
      <span>
        Generated {date(at.slice(0, 10))}, {time(at, tz)} · Hearth
      </span>
      <span>Derived from the punch log; the totals are the ones payroll uses.</span>
    </footer>
  );
}

// ---------------------------------------------------------------- Monthly timesheet

export function TimesheetPrint() {
  const { employeeId } = useParams({ strict: false }) as { employeeId: string };
  const search = useSearch({ strict: false }) as ReturnType<typeof printSearch>;
  const navigate = useNavigate();
  const me = useMe().data!;
  const month = search.month ?? todayIn(me.employee?.timeZone ?? undefined).slice(0, 7);
  const basis = search.basis ?? 'payroll';
  const set = (next: { month?: string; basis?: Basis }) => void navigate({ to: '/attendance/$employeeId/print', params: { employeeId }, search: { month, basis, ...next }, replace: true });
  const q = useQuery({ queryKey: ['timesheet-print', employeeId, month, basis], queryFn: () => get<Sheet>(`/api/attendance/reports/timesheet${qs({ employeeId, month, basis })}`) });
  const s = q.data;

  return (
    <div>
      <div className="row row--between row--wrap no-print" style={{ margin: 'var(--s-6) 0' }}>
        <BackLink employeeId={employeeId} />
        <div className="row row--wrap">
          <Segmented label="Period" idPrefix="ts-basis" value={basis} onChange={(b) => set({ basis: b })} options={[{ id: 'payroll', label: 'Payroll period' }, { id: 'calendar', label: 'Calendar month' }]} />
          <div className="row" style={{ ['--gap' as string]: '4px' }}>
            <Button variant="ghost" size="sm" iconOnly icon="chevronLeft" aria-label="Previous month" onClick={() => set({ month: shiftMonth(month, -1) })} />
            <span className="mono" style={{ minWidth: 64, textAlign: 'center' }}>
              {month}
            </span>
            <Button variant="ghost" size="sm" iconOnly icon="chevronRight" aria-label="Next month" onClick={() => set({ month: shiftMonth(month, 1) })} />
          </div>
          <Link to="/attendance/$employeeId/recap" params={{ employeeId }} search={{ year: Number(month.slice(0, 4)) }} className="btn btn--ghost btn--sm">
            <Icon name="roster" />
            Year recap
          </Link>
          <Button size="sm" icon="download" disabled={!s} onClick={() => window.print()}>
            Print / PDF
          </Button>
        </div>
      </div>
      {q.isLoading ? (
        <Skeleton h={720} />
      ) : !s ? (
        <Callout tone="bad">{q.error instanceof ApiError ? q.error.message : 'This timesheet is not available.'}</Callout>
      ) : (
        <TimesheetDoc s={s} basis={basis} />
      )}
    </div>
  );
}

function TimesheetDoc({ s, basis }: { s: Sheet; basis: Basis }) {
  const t = s.totals;
  const leaveTypes = Object.entries(t.leaveByType);
  return (
    <article className="ts-doc">
      <DocHead kind="Attendance timesheet" period={{ label: basis === 'payroll' ? 'Payroll period' : 'Month', value: dateRange(s.from, s.to) }} />
      <PersonBlock e={s.employee} />
      <dl className="ts-totals">
        <div>
          <dt>Present</dt>
          <dd>
            {t.present}
            <small>/ {t.workingDays - t.upcoming}</small>
          </dd>
        </div>
        <div data-tone={t.absent ? 'bad' : undefined}>
          <dt>Absent</dt>
          <dd>{t.absent}</dd>
        </div>
        <div data-tone={t.lateMinutes ? 'warn' : undefined}>
          <dt>Late in</dt>
          <dd>
            {t.lateMinutes}
            <small>min</small>
          </dd>
        </div>
        <div data-tone={t.earlyMinutes ? 'warn' : undefined}>
          <dt>Early out</dt>
          <dd>
            {t.earlyMinutes}
            <small>min</small>
          </dd>
        </div>
        <div>
          <dt>Leave</dt>
          <dd>
            {t.leave}
            {leaveTypes.length ? <small>{leaveTypes.map(([c, n]) => `${c} ${n}`).join(' · ')}</small> : null}
          </dd>
        </div>
        <div>
          <dt>Overtime</dt>
          <dd>
            {hours(t.overtimeMinutes)}
            <small>h</small>
          </dd>
        </div>
        <div>
          <dt>Worked</dt>
          <dd>
            {hm(t.workedMinutes)}
            <small>h</small>
          </dd>
        </div>
      </dl>
      {t.upcoming ? <p className="ts-note no-print">This period is not over: {t.upcoming} scheduled work {t.upcoming === 1 ? 'day is' : 'days are'} still to come.</p> : null}
      <div className="ts-table-wrap">
        <table className="ts-table">
          <thead>
            <tr>
              <th className="l">Date</th>
              <th>Day</th>
              <th title="Absent, day off, holiday or leave type">*</th>
              <th>In</th>
              <th>Out</th>
              <th>Work hrs</th>
              <th>In-late</th>
              <th>Out-early</th>
              <th>Shift</th>
              <th className="l">Valid</th>
            </tr>
          </thead>
          <tbody>
            {s.days.map((d) => {
              const blank = d.kind === 'upcoming' || d.kind === 'not_employed';
              return (
                <tr key={d.date} className={`ts-row--${d.kind}`}>
                  <td className="l">{date(d.date, { year: false })}</td>
                  <td>{dow(d.date)}</td>
                  <td className="ts-mark">{d.mark ?? ''}</td>
                  <td className={d.lateMinutes ? 'ts-bad' : undefined}>{d.clockIn ?? (blank ? '' : '—')}</td>
                  <td className={d.earlyMinutes ? 'ts-bad' : undefined}>
                    {d.clockOut ?? (blank ? '' : '—')}
                    {d.clockOutNextDay ? <sup title="Next day">+1</sup> : null}
                  </td>
                  <td>{d.workedMinutes ? hm(d.workedMinutes) : blank ? '' : '—'}</td>
                  <td className={d.lateMinutes ? 'ts-bad' : 'ts-dim'}>{d.lateMinutes || (blank ? '' : '—')}</td>
                  <td className={d.earlyMinutes ? 'ts-bad' : 'ts-dim'}>{d.earlyMinutes || (blank ? '' : '—')}</td>
                  <td className="mono" title={d.shift ? `${d.shift.name} ${d.shift.start}–${d.shift.end}` : undefined}>
                    {d.shift?.code ?? ''}
                  </td>
                  <td className="l">
                    {d.note}
                    {d.overtimeMinutes ? <span className="ts-dim"> · OT {hours(d.overtimeMinutes)} h</span> : null}
                  </td>
                </tr>
              );
            })}
          </tbody>
          <tfoot>
            <tr>
              <td className="l" colSpan={5}>
                Total · {t.present} present · {t.absent} absent · {t.leave} leave
              </td>
              <td>{hm(t.workedMinutes)}</td>
              <td>{t.lateMinutes}</td>
              <td>{t.earlyMinutes}</td>
              <td colSpan={2} />
            </tr>
          </tfoot>
        </table>
      </div>
      <Legend items={s.legend} />
      <Generated at={s.generatedAt} tz={s.employee.office.timeZone} />
    </article>
  );
}

// ---------------------------------------------------------------- Yearly recap

export function TimesheetRecap() {
  const { employeeId } = useParams({ strict: false }) as { employeeId: string };
  const search = useSearch({ strict: false }) as ReturnType<typeof recapSearch>;
  const navigate = useNavigate();
  const me = useMe().data!;
  const year = search.year ?? Number(todayIn(me.employee?.timeZone ?? undefined).slice(0, 4));
  const setYear = (y: number) => void navigate({ to: '/attendance/$employeeId/recap', params: { employeeId }, search: { year: y }, replace: true });
  const q = useQuery({ queryKey: ['timesheet-recap', employeeId, year], queryFn: () => get<Recap>(`/api/attendance/reports/recap${qs({ employeeId, year })}`) });
  const r = q.data;

  return (
    <div>
      <div className="row row--between row--wrap no-print" style={{ margin: 'var(--s-6) 0' }}>
        <Link to="/attendance/$employeeId/print" params={{ employeeId }} search={{ month: `${year}-${todayIn(me.employee?.timeZone ?? undefined).slice(5, 7)}` }} className="link faint">
          ← Monthly timesheet
        </Link>
        <div className="row row--wrap">
          <div className="row" style={{ ['--gap' as string]: '4px' }}>
            <Button variant="ghost" size="sm" iconOnly icon="chevronLeft" aria-label="Previous year" onClick={() => setYear(year - 1)} />
            <span className="mono" style={{ minWidth: 48, textAlign: 'center' }}>
              {year}
            </span>
            <Button variant="ghost" size="sm" iconOnly icon="chevronRight" aria-label="Next year" onClick={() => setYear(year + 1)} />
          </div>
          <Button size="sm" variant="ghost" icon="download" disabled={!r} onClick={() => window.location.assign(`/api/attendance/reports/recap.xlsx${qs({ employeeId, year })}`)}>
            Excel
          </Button>
          <Button size="sm" icon="download" disabled={!r} onClick={() => window.print()}>
            Print / PDF
          </Button>
        </div>
      </div>
      {q.isLoading ? (
        <Skeleton h={560} />
      ) : !r ? (
        <Callout tone="bad">{q.error instanceof ApiError ? q.error.message : 'This recap is not available.'}</Callout>
      ) : (
        <article className="ts-doc recap-doc">
          <DocHead kind="Attendance recap" period={{ label: 'Year', value: String(r.year) }} />
          <PersonBlock e={r.employee} />
          <div className="recap-wrap">
            <table className="recap-grid">
              <thead>
                <tr>
                  <th className="recap-grid__m">Month</th>
                  {Array.from({ length: 31 }, (_, k) => (
                    <th key={k}>{k + 1}</th>
                  ))}
                  {r.codes.map((c) => (
                    <th key={c} className="recap-grid__sub">
                      {c}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {r.months.map((m) => (
                  <tr key={m.month}>
                    <th className="recap-grid__m">{m.label}</th>
                    {m.cells.map((c, k) => (
                      <td key={k} data-kind={c?.kind ?? 'none'} data-code={c?.code || undefined}>
                        {c?.code ?? ''}
                      </td>
                    ))}
                    {r.codes.map((c) => (
                      <td key={c} className="recap-grid__sub">
                        {m.counts[c] || ''}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr>
                  <th className="recap-grid__m">Total</th>
                  <td colSpan={31} />
                  {r.codes.map((c) => (
                    <td key={c} className="recap-grid__sub">
                      {r.totals[c] ?? 0}
                    </td>
                  ))}
                </tr>
              </tfoot>
            </table>
          </div>
          <Legend items={r.legend} />
          <Generated at={r.generatedAt} tz={r.employee.office.timeZone} />
        </article>
      )}
    </div>
  );
}
